import { cn } from "@/lib/utils";
import { ArrowUp, ArrowDown, Pencil, Eye, EyeOff, Copy, Trash2, MoreVertical } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TYPE_LABEL = {
  hero: "Hero", rich_text: "Text", image_text: "Image + Text", cta_band: "CTA Band",
  feature_grid: "Feature Grid", gallery: "Gallery", embed: "Functional section",
};

// Wraps a rendered section with an in-context editing toolbar (staff only, edit mode).
export const SectionFrame = ({
  section, index, total, selected,
  onEdit, onUp, onDown, onToggleVisible, onDuplicate, onDelete, children,
}) => {
  const editable = section.type !== "embed";
  const hidden = section.visible === false;
  const heroish = section.type === "hero";
  const iconBtn = "flex h-8 w-8 items-center justify-center rounded-md text-white/90 hover:bg-white/15 disabled:opacity-30 disabled:hover:bg-transparent transition-colors";

  return (
    <div
      className={cn(
        "group/se relative",
        selected && "outline outline-2 outline-[hsl(var(--brand-red))] outline-offset-[-2px]",
        hidden && "opacity-50"
      )}
      data-testid={`section-frame-${index}`}
    >
      {hidden && (
        <span className="absolute left-3 top-3 z-[45] rounded bg-black/70 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
          Hidden
        </span>
      )}

      <div className="pointer-events-none absolute right-3 top-3 z-[45] flex items-center gap-1 rounded-lg border border-white/15 bg-black/70 p-1 opacity-80 shadow-lg backdrop-blur transition-opacity duration-200 group-hover/se:opacity-100 focus-within:opacity-100 [&>*]:pointer-events-auto">
        <button onClick={onUp} disabled={index === 0} title="Move up" data-testid={`frame-up-${index}`} className={iconBtn}><ArrowUp className="h-4 w-4" /></button>
        <button onClick={onDown} disabled={index === total - 1} title="Move down" data-testid={`frame-down-${index}`} className={iconBtn}><ArrowDown className="h-4 w-4" /></button>
        {editable ? (
          <button
            onClick={onEdit}
            data-testid={`frame-edit-${index}`}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white transition-colors hover:bg-[hsl(var(--brand-red-deep))]"
          >
            <Pencil className="h-3.5 w-3.5" /> {heroish ? "Edit hero" : "Edit"}
          </button>
        ) : (
          <span className="px-2 text-[11px] font-medium uppercase tracking-wide text-white/60">{TYPE_LABEL[section.type]}</span>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button title="More" data-testid={`frame-more-${index}`} className={iconBtn}><MoreVertical className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="z-[80]">
            <DropdownMenuItem onClick={onToggleVisible} data-testid={`frame-visible-${index}`}>
              {hidden ? <><Eye className="mr-2 h-4 w-4" /> Show on site</> : <><EyeOff className="mr-2 h-4 w-4" /> Hide from site</>}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDuplicate} data-testid={`frame-duplicate-${index}`}><Copy className="mr-2 h-4 w-4" /> Duplicate</DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive" data-testid={`frame-delete-${index}`}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {children}
    </div>
  );
};
